// Core type definitions for BikeDash beta

// ============================================================================
// User & Auth
// ============================================================================

export interface User {
  id: string;
  username: string;
  email: string;
  avatar?: string;
  isAdmin?: boolean;
  status?: "online" | "offline" | "invisible"; // User-set status
  lastSeen?: number; // Unix timestamp of last activity (always updated)
  lastOnline?: number; // Unix timestamp of last time status was "online"
  mapIcon?: string; // Emoji icon for map display (e.g., "🚴", "🏃", "🚲")
  mapColor?: string; // Background color for map icon circle (e.g., "#FF5733")
}

// Cached locally for offline access
export interface LocalUserData {
  user: User;
  token?: string;
  refreshToken?: string;
  apiKeys?: Record<string, string>; // { 'thunderforest': 'key123', 'openweather': 'key456' }
  lastSync: number;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isGuest: boolean;
  error: string | null;
}

// ============================================================================
// GPS & Position
// ============================================================================

// Note: We use [lng, lat] order to match GeoJSON spec and most map libraries
export type Coordinate = [number, number]; // [lng, lat] - simple coordinate pair

// Tracking mode for GPS source selection
export type TrackingMode = "device" | "simulation" | "auto";

// Data structure from tracking providers
export interface TrackingData {
  position: Position | null;
  isActive: boolean;
  source: "device" | "simulation" | "bluetooth" | "unknown";
  metadata?: {
    accuracy?: number;
    heading?: number;
    compassHeading?: number;
    speed?: number;
    timestamp?: number;
  };
}

// Position can represent partial updates (e.g., just heading without location)
// This allows efficient streaming of sensor data
export interface Position {
  lat?: number; // Can be undefined for partial updates
  lng?: number; // Can be undefined for partial updates
  accuracy?: number; // meters
  heading?: number; // degrees, GPS-derived (from movement)
  headingUnreliable?: boolean; // True when heading is unreliable (show dot), false/undefined when reliable (show arrow)
  compassHeading?: number; // degrees, magnetic compass
  speed?: number; // m/s
  altitude?: number; // meters
  timestamp: number; // Unix timestamp in ms
  isComplete?: boolean; // True when we have full GPS fix with lat/lng
  isInterpolated?: boolean; // True if this is a smoothed/interpolated point
  isIdle?: boolean; // True if this position marks an idle period (no movement for 15+ seconds)
  isPaused?: boolean; // True if this position marks where the user explicitly paused
  isInactive?: boolean; // True if this position marks where the app was backgrounded/closed (inactive for >5 min or >1 mile jump)
}

// TrackPoint is used for storing historical Position data with timestamp wrapper
// This structure is used in OPFS JSONL files for efficient activity storage
export interface TrackPoint {
  timestamp: number;
  position: Position;
}

// ============================================================================
// Activity
// ============================================================================

export type ActivityState = "idle" | "recording" | "paused";

// Activity event log - stores all events during activity
// Events are streamed to JSONL during recording for crash recovery
export interface Note {
  text: string;
  position?: Position; // Where the note was added
}

export interface Photo {
  url: string; // Path or URL
  caption?: string;
  position?: Position; // Where photo was taken
  width?: number;
  height?: number;
}

export interface ActivityEvent {
  timestamp: number;
  type:
    | "start"
    | "pause"
    | "resume"
    | "stop"
    | "position"
    | "place_visited"
    | "note"
    | "photo";
  position?: Position; // For 'position' events
  place?: Place; // For 'place_visited' events
  note?: Note; // For 'note' events
  photo?: Photo; // For 'photo' events
  metrics?: ActivityMetrics; // Current metrics at this point
}

// Activity summary - track points and events are stored separately in JSONL
// This keeps the summary small for fast listing/searching
export interface Activity {
  id: string;
  title?: string;
  description?: string;
  tags?: string[]; // ['training', 'commute', 'leisure', etc.]
  startTime: number; // Unix timestamp in ms
  endTime?: number;
  distance: number; // meters
  avgSpeed: number; // m/s
  maxSpeed: number; // m/s
  pausedDuration: number; // ms
  places?: Place[]; // Full copies of places visited (denormalized for offline)
  events?: ActivityEvent[]; // Full event log (loaded from JSONL when needed)
  trackPoints?: TrackPoint[]; // Track points (loaded from JSONL when needed)
  syncStatus?: "local" | "syncing" | "synced";
  isDeleted?: boolean; // Soft delete for sync
  lastModified?: number; // Unix timestamp for conflict resolution
  excluded?: boolean; // Exclude from total stats calculations
  savedAsRouteId?: string; // Reference to route created from this activity
}

export interface ActivityMetrics {
  distance: number;
  duration: number;
  currentSpeed: number;
  avgSpeed: number;
  maxSpeed: number;
}

// ============================================================================
// Map
// ============================================================================

export interface MapStyle {
  id: string; // Format: 'provider:style' e.g. 'osm:standard', 'thunderforest:cycle'
  name: string;
  url: string;
  requiresKey: boolean;
  layerType?: "base" | "labels" | "roads"; // What this layer provides
}

// Available map styles with their IDs
export const MAP_STYLE_IDS = [
  // Thunderforest (requires API key)
  "thunderforest:cycle",
  "thunderforest:landscape",
  "thunderforest:outdoors",
  "thunderforest:transport",
  "thunderforest:transport-dark",
  "thunderforest:spinal-map",
  "thunderforest:pioneer",
  "thunderforest:mobile-atlas",
  "thunderforest:neighbourhood",
  // Free maps
  "osm:standard",
  "osm:humanitarian",
  "osm:topographic",
  "cartodb:light",
  "cartodb:dark",
  "cartodb:voyager",
  "esri:gray",
  "esri:satellite",
] as const;

export type MapStyleId = (typeof MAP_STYLE_IDS)[number];

export type MapMode = "free" | "follow" | "follow-north" | "follow-heading"; // Free drag, follow GPS, follow GPS north-locked, follow with compass rotation

export interface MapView {
  center: Coordinate; // [lng, lat]
  zoom: number;
  bearing: number; // Current rotation
  mode: MapMode; // Controls GPS tracking and rotation
  baseMapId?: string; // e.g., satellite imagery
  overlayMapId?: string; // e.g., street labels/roads
  overlayOpacity?: number; // 0-1 for overlay transparency
}

// Persistent overlay settings (saved in preferences)
export interface MapOverlaySettings {
  showStarredPlaces: boolean; // Always show starred places
  showFriends: boolean; // Show nearby friends
  showCompass: boolean; // Show compass overlay
  showScale: boolean; // Show distance scale
  showDebugInfo: boolean; // Show lat/lon/heading/accuracy etc
}

// Temporary overlays (runtime only, not saved)
export interface MapOverlays {
  searchResults?: boolean; // Show current search results
  camera?: boolean; // Camera feed overlay
  bluetoothCamera?: boolean; // Bluetooth camera feed
  routePreview?: boolean; // Previewing a route
}

// ============================================================================
// Routes
// ============================================================================

export interface Waypoint {
  position?: Position; // GPS coordinate
  place?: Place; // Or a saved place (can be point, line, or polygon)
  name?: string;
  note?: string;
  action?: string; // 'turn-left', 'turn-right', 'continue', 'rest', 'photo-spot', etc.
  instruction?: string; // "Turn left onto Main St"
  proximity?: number; // Trigger radius in meters (e.g., announce at 50m)
  icon?: string;
  targetTime?: number; // For time-based goals
  media?: Photo[]; // Photos or other media
}

export interface Route {
  id: string;
  name: string;
  description?: string;
  waypoints: (Coordinate | Position | Place | Waypoint)[]; // Flexible: simple coords, GPS positions, places, or rich waypoints
  trackPoints?: Position[]; // Extracted points for drawing the route line
  totalDistance?: number; // meters, calculated
  estimatedTime?: number; // seconds, calculated
  elevationGain?: number;
  createdAt: number;
  tags?: string[];
  isPublic?: boolean;
  isDeleted?: boolean; // For sync
  lastModified?: number;
  // Runtime display data
  geometry?: any[]; // OpenLayers display elements (for restoration)
  centerLat?: number; // Center position for distance calculations
  centerLon?: number;
  sourceActivityId?: string; // Reference to original activity this route was created from
}

// Route service state for active route tracking
export interface RouteState {
  isActive: boolean;
  routeName: string | null;
  routeId: string | null;
  totalDistance: number; // meters
  estimatedTime: number; // seconds
  startTime: number | null; // timestamp
  geometry: any[] | null; // OpenLayers display elements
  isSaved: boolean; // Whether this route is saved
  centerLat?: number; // Center position
  centerLon?: number;
}

export interface RouteProgress {
  routeId: string;
  currentWaypointIndex: number;
  distanceToNext: number; // meters to next waypoint
  distanceCompleted: number;
  distanceRemaining: number;
  offRoute?: boolean; // If user has deviated
  deviationDistance?: number; // How far off route
}

// ============================================================================
// Places & Search
// ============================================================================

export type PlaceGeometry =
  | { type: "point"; position: Coordinate }
  | { type: "line"; positions: Coordinate[] } // Road, trail, etc.
  | { type: "polygon"; positions: Coordinate[] }; // Park, area, etc.

// Place can represent both ephemeral search results and saved places
// When from Nominatim search: has osmId, no sync fields
// When saved: has createdAt, sync fields
export interface Place {
  id: string; // Our ID or osm_id
  name: string; // Short name for display
  title?: string; // Optional override/longer title
  description?: string; // Detailed description
  geometry: PlaceGeometry; // Point, line, or polygon
  category?: string; // From OSM type/class or user-defined
  tags?: string[]; // User-added tags: ['wifi', 'bike-parking', 'water', etc.]
  markerColor?: string; // Custom color for marker/line/fill
  markerIcon?: string; // Custom icon (for points)
  address?: string; // Full address/display_name from Nominatim
  notes?: string; // User notes

  // OSM/Nominatim data (when from search)
  osmId?: string;
  osmType?: string;
  importance?: number;

  // Sync fields (only for saved places)
  createdAt?: number; // Only when saved
  isDeleted?: boolean; // Soft delete for sync
  lastModified?: number;
  isSaved?: boolean; // Explicitly mark as saved vs ephemeral
}

export interface SavedPlaces {
  places: Place[];
  lastUpdated: number;
}

// Search result - can be converted to Place when saved
// Enhanced with OSM data from Nominatim/Overpass APIs
export interface SearchResult {
  // Nominatim fields
  place_id: string | number; // Can be numeric from Nominatim API
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  class?: string; // Optional - OSM feature class (highway, leisure, etc.)
  importance: number;
  boundingbox?: string[];

  // OSM data
  osm_type?: string;
  osm_id?: string | number;
  geometry?: any[]; // GeoJSON geometry from Overpass
  tags?: any; // OSM tags
  geojson?: any; // Full GeoJSON object

  // Computed fields
  wayLength?: number; // Length in meters for ways/paths
  distance?: number; // meters from current position

  // Optional Place conversion
  place?: Place; // Full Place object if converted
}

// Search history for recent searches
export interface SearchHistoryItem {
  id: string;
  query: string;
  selectedResult?: SearchResult; // What user actually selected
  timestamp: number;
}

// Conflict resolution for sync
export interface ConflictResolution {
  strategy: "local-wins" | "remote-wins" | "newest-wins" | "manual";
  conflictedItem?: Activity | Place | Route;
  remoteVersion?: Activity | Place | Route;
}

// Offline cache status
export interface CacheStatus {
  mapTiles: {
    // Note: Using browser cache for now, don't implement custom tile caching yet
    count: number;
    sizeBytes: number;
    lastUpdated: number;
  };
  activities: {
    pendingSync: number;
    cachedCount: number;
  };
  places: {
    pendingSync: number;
    cachedCount: number;
  };
}

// ============================================================================
// Friends
// ============================================================================

export interface Friend {
  id: string;
  userId: string; // The friend's user ID
  username: string;
  email?: string;
  avatar?: string;
  status: "online" | "offline" | "riding"; // Computed status based on user's actual status and activity
  userStatus?: "online" | "offline" | "invisible"; // User's self-set status
  lastSeen?: number; // Unix timestamp (only visible if friends)
  lastOnline?: number; // Last time they were in "online" status (shown when offline/invisible)
  lastPing?: number; // Last time we got a presence update
  addedAt: number; // Unix timestamp when friendship was established
  // Sharing settings
  shareActivities?: boolean; // Share activity summaries with this friend
  shareLocation?: boolean; // Share live location with this friend
  shareStatus?: boolean; // Share online/offline status with this friend (defaults to true)
  shareRoutes?: boolean; // Share saved routes with this friend
  shareMode?: "always" | "proximity" | "activity" | "manual"; // How location is shared
  proximityRadius?: number; // meters, if mode is proximity
  // Map display settings
  defaultMapIcon?: string; // Their profile icon (what they set for themselves)
  defaultMapColor?: string; // Their profile color (what they set for themselves)
  mapIcon?: string; // Your custom override icon for this friend on YOUR map
  mapColor?: string; // Your custom override color for this friend on YOUR map
}

export interface FriendRequest {
  id: string;
  fromUserId: string;
  toUserId: string;
  fromUsername: string;
  fromEmail?: string;
  fromAvatar?: string;
  toUsername?: string; // Target user's username (for outgoing requests)
  toEmail?: string; // Target user's email (for outgoing requests)
  invitedEmail?: string; // Email/identifier used to send the invite
  invitedUsername?: string; // Username used to send the invite
  viaInviteCode?: boolean; // True if request came from invite code
  pendingUser?: boolean; // True if target user hasn't joined yet
  status: "pending" | "accepted" | "declined";
  createdAt: number; // Unix timestamp
  expiresAt?: number; // Optional expiry
  direction?: "incoming" | "outgoing"; // Direction of the request
}

export interface InviteCodeUsage {
  userId: string;
  username: string;
  email: string;
  usedAt: number;
}

export interface InviteCode {
  id: string; // Unique code ID
  code: string; // 6-digit code
  userId?: string; // User who created the code
  createdAt: number;
  expiresAt: number; // 24 hours from creation (renewable)
  maxUses: number | null; // null = unlimited, number = limited uses
  useCount: number; // How many times it's been used
  usedBy: InviteCodeUsage[]; // List of users who used this code
  isActive: boolean; // Active or deactivated
}

export interface LiveShare {
  userId: string;
  position: Position;
  activityId?: string; // If sharing during activity
  shareMode: "always" | "proximity" | "activity" | "manual";
  proximityRadius?: number; // meters, if mode is proximity
  sharedWith: string[]; // user IDs or 'all-friends'
  expiresAt?: number; // Optional expiry
}

// ============================================================================
// Preferences
// ============================================================================

export type Theme =
  | "light"
  | "dark"
  | "high-contrast-light"
  | "high-contrast-dark"
  | "auto";
export type MarkerStyle = "arrow" | "circle" | "bike" | "triangle";

export interface TrackDisplaySettings {
  showLine: boolean;
  lineWidth: number; // pixels
  lineColor: string; // Default track line color (supports rgba)
  lineOpacity?: number; // 0-1
  showOutline?: boolean; // Show white outline around line
  outlineColor?: string; // Outline color ("auto", "none", or color value)
  showPoints?: boolean; // Show individual track points
  pointsInterval?: number; // Show every Nth point
  showDirection?: boolean; // Show direction arrows
  gradientBySpeed?: boolean; // Color gradient based on speed
  fadeOldSegments?: boolean; // Fade older parts of track
}

export interface MapPreferences {
  defaultStyleId: string;
  starredStyleIds: string[];
  satelliteUnderlay: boolean; // Use satellite as base with selected map as overlay
  overlayOpacity?: number; // Opacity of the overlay map (0-1)
  markerStyle: MarkerStyle;
  markerColor?: string;
  autoFollow: boolean;
  smoothMotion: boolean; // Enable GPS interpolation for smooth movement
  useGpsHeading?: boolean; // Use GPS-derived heading when speed > 5mph (avoids motion permission on iOS)
  activityTrack?: TrackDisplaySettings;
  routeTrack?: TrackDisplaySettings; // Style for simulation/navigation routes
  overlaySettings?: MapOverlaySettings; // What overlays to show
  zoomLevel?: number; // Persisted zoom level
  mode?: MapMode; // Persisted map mode (free, follow, follow-heading)
  lastCenter?: [number, number]; // Last known map center [lng, lat]
}

export interface UserPreferences {
  // Display
  units: "metric" | "imperial";
  theme: Theme;
  activityTheme?: Theme;
  activityBarLayout?: "compact" | "expanded" | "overlay";
  textSize?: "small" | "medium" | "large";
  keepScreenOn: boolean;

  // Privacy
  defaultActivityPrivacy?: "private" | "friends" | "public";
  shareLocationWithFriends?: boolean;
  proximityAlertsEnabled?: boolean;
  proximityRadius?: number; // meters

  // Activity
  autoSaveInterval?: number; // Seconds between auto-saves
  inactivityTimeout?: number; // Minutes of inactivity before auto-pause (0 = disabled, 1-30)
  inactivityDistance?: number; // Kilometers - GPS jump threshold for auto-pause (0 = disabled)
  confirmationMode?: "never" | "stop" | "both"; // Activity confirmation mode (default: "stop")
  voiceAnnouncements?: boolean; // Turn-by-turn voice
  announcementVolume?: number; // 0-100

  // Data & Sync
  offlineMode?: boolean; // Force offline, no network requests
  autoSync?: boolean; // When online, sync automatically
  syncOverCellular?: boolean;
  offlineMapRadius?: number; // km to cache around routes
  apiKeys?: Record<string, string>; // All API keys (maps, weather, search, etc.)

  // Performance
  maxUpdateRate?: number; // Max FPS for all animation updates (1-60fps)

  // Simulation
  resumeSimulationOnReload?: boolean; // Resume simulation when reloading page

  // Updates
  autoUpdate?: boolean; // Auto-prepare updates in background (won't reload until next app open)

  // Camera
  cameraEnabled?: boolean; // Enable camera overlay
  cameraType?: "bluetooth" | "phone"; // Camera source type
}

// ============================================================================
// State & Sync
// ============================================================================

export interface SessionState {
  activityId?: string;
  state: ActivityState;
  lastPosition?: Position;
  metrics: ActivityMetrics;
  lastUpdate: number;
}

export interface SyncItem {
  id: string;
  type: "activity" | "places" | "settings" | "preferences";
  action: "create" | "update" | "delete" | "replace";
  data: any;
  timestamp: number;
  version?: number; // For conflict resolution
}

// ============================================================================
// API
// ============================================================================

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

// ============================================================================
// Events
// ============================================================================

export type LocationUpdateHandler = (position: Position) => void;
export type ActivityUpdateHandler = (metrics: ActivityMetrics) => void;

export interface Subscription {
  unsubscribe: () => void;
}

// ============================================================================
// PWA & Service Worker
// ============================================================================

export interface CacheStrategy {
  name: "cache-first" | "network-first" | "stale-while-revalidate";
  ttl?: number; // Time to live in seconds
}

export interface ServiceWorkerState {
  isInstalled: boolean;
  isUpdating: boolean;
  updateAvailable: boolean;
}

// ============================================================================
// OPFS Storage
// ============================================================================

export interface OPFSFile {
  path: string;
  handle?: FileSystemFileHandle;
  lastModified: number;
  size: number;
}

export interface OPFSDirectory {
  path: string;
  handle?: FileSystemDirectoryHandle;
  files: OPFSFile[];
  subdirectories: OPFSDirectory[];
}

// ============================================================================
// Map Data Source (dual-track system)
// ============================================================================

// Central data broker between GPS and map display
// Manages both real GPS points (1Hz) and interpolated smooth motion (5Hz)
export interface MapDataSource {
  realTrackPoints: Position[]; // Actual GPS points (1Hz)
  smoothedTrackPoints: Position[]; // Interpolated points (5Hz, last 30 seconds)
  combinedTrack: Position[]; // Older real + recent smoothed for display
  currentPosition: Position | null;
  lastUpdate: number;
  subscribers: Set<(data: MapDataSource) => void>;
}

// ============================================================================
// Device & Debug
// ============================================================================

export interface DeviceInfo {
  deviceId: string;
  platform: string; // 'ios' | 'android' | 'web'
  model?: string;
  os?: string;
  appVersion: string;
  screenSize?: { width: number; height: number };
}

export interface DebugSettings {
  enabled: boolean;
  namespaces: string[]; // ['bikedash:map:*', etc.]
  logLevel: "error" | "warn" | "info" | "debug";
  showPerformance: boolean;
}

// Replay/Ghost feature (available to all users)
// Can replay activities/routes for virtual racing or GPS simulation
export interface ReplayState {
  isActive: boolean;
  isPaused: boolean;
  mode: "watch" | "race" | "simulate"; // Watch = just replay, Race = time trial against it, Simulate = fake GPS (debug)
  source: "activity" | "route" | "custom";
  activityId?: string; // If replaying an activity
  routeId?: string; // If replaying a route
  customTrack?: (Coordinate | Position)[]; // Custom track or simulation data
  currentIndex: number;
  playbackSpeed: number; // 1 = real-time, 2 = 2x speed, etc.
  loop: boolean; // Loop at end
  showGhost?: boolean; // Show ghost/replay on map
  showComparison?: boolean; // Show timing comparison
  startDelay?: number; // Seconds to delay ghost start (positive = ghost ahead, negative = ghost behind)
  // Simulation options (only in debug mode)
  // Import from simulationService for full type
  simulationOptions?: {
    addNoise: boolean;
    varySpeed: boolean;
    fakeCompass: boolean;
    speedVariation: number;
    noiseLevel: number;
    updateRate: number;
    speedMultiplier: number;
  };
}

// ============================================================================
// Export Formats
// ============================================================================

export interface GPXExport {
  version: string;
  creator: string;
  metadata?: {
    name?: string;
    desc?: string;
    time?: string;
  };
  tracks: Array<{
    name?: string;
    segments: Array<{
      points: Array<{
        lat: number;
        lon: number;
        ele?: number;
        time?: string;
      }>;
    }>;
  }>;
}

// ============================================================================
// Constants
// ============================================================================

// OPFS directory structure
// local/ - device-specific (persists across users)
// users/{id}/ - user data (activities, places, etc.)
// users/guest/ - guest data (before login)
export const STORAGE_PATHS = {
  local: "local",
  users: "users",
  guest: "users/guest",
} as const;

export const GPS_CONFIG = {
  highAccuracy: true,
  timeout: 10000, // ms
  minAccuracy: 50, // meters
  minDistance: 5, // meters
  interpolationRate: 5, // Hz
} as const;

export const RECOVERY_THRESHOLDS = {
  autoResume: { minutes: 5, meters: 100 },
  promptResume: { minutes: 30, meters: 500 },
} as const;

export const MAP_DEFAULTS = {
  center: [-97.7431, 30.2672] as Coordinate, // Austin, TX
  zoom: 13,
  minZoom: 3,
  maxZoom: 19,
  bearing: 0,
  pitch: 0,
  defaultStyle: "osm:standard" as MapStyleId,
} as const;
